import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'
import { useBrandStore } from '@/stores/brand'
import { getCached, setCached, invalidateCache } from '@/lib/query-cache'
import { publishTenantAppIdentity } from '@/lib/tenant-app-identity'

/**
 * The installed-app identity for the SIGNED-IN viewer, on every route.
 *
 * ## The gap this closes
 *
 * Task 55 built the manifest endpoint and pointed it at a surface almost no
 * signed-in user reaches. `applyTenantAppIdentity` had exactly one caller,
 * `usePublicEventBrand`, gated on an invitation token — and signed-in viewers
 * are redirected off `/event/public/:token` to `/event/:id`, where the token is
 * `undefined` and the whole chain no-ops. Measured 10.08.2026 as a real Nortura
 * participant on `/event/:id`: `manifest: /manifest.webmanifest`,
 * `apple-mobile-web-app-title: EventRipple`, `data-tenant-app-icon: null`.
 *
 * The account is the missing half. A tenant's people are signed in on
 * `/dashboard`, `/events` and `/event/:id`, and those pages know who the viewer
 * is but nothing about a token.
 *
 * ## Why the resolution is in SQL and not here
 *
 * `get_app_identity_for_me()` answers it in one round trip, as definer, with
 * the precedence written down once: active org → oldest org membership →
 * personal brand → the single tenant whose events you take part in. The last
 * step is the one that matters in practice. Measured 13.08.2026: 84 users have
 * an RSVP on a Nortura event, and only 6 are in `organization_members` — the
 * other 78 are participants, and they are most of who the feature is for.
 *
 * Doing that client-side would be three or four round trips and a second copy
 * of a precedence rule that `fetchBrandSettingsForOwner` already states.
 *
 * ## No row is an answer
 *
 * No row means EventRipple's own identity, and so does a row whose
 * `manifest_query` is null — an owner-scoped brand (Compass Group,
 * TOLLGAARDEN) has no slug, so the slug endpoint cannot reach it and there is
 * no honest query to point the link at. Both publish `null`, which leaves the
 * document's build-time manifest exactly as it is. A failed request does the
 * same: this is decoration on top of a working default, never a gate in front
 * of one.
 */

const IDENTITY_TTL_MS = 5 * 60_000
const cacheKey = (userId: string) => `app_identity:${userId}`

interface AppIdentityRow {
  tenant_key: string | null
  manifest_query: string | null
  name: string | null
  short_name: string | null
  app_icon_url: string | null
  theme_color: string | null
}

/** Exported for the test, and for anything that later needs the raw row. */
export async function fetchAppIdentityForMe(userId: string): Promise<AppIdentityRow | null> {
  const key = cacheKey(userId)
  const cached = getCached<AppIdentityRow | null>(key, IDENTITY_TTL_MS)
  if (cached !== null) return cached
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)('get_app_identity_for_me')
  if (error) return null
  const row = (Array.isArray(data) ? data[0] : null) as AppIdentityRow | null
  setCached(key, row ?? null)
  return row
}

export function useTenantAppIdentity(): void {
  const userId = useAuthStore((s) => s.user?.id)
  // An admin who renames their app in Branding should see it without a reload.
  // Same signal AppLayout re-applies the palette on.
  const brandVersion = useBrandStore((s) => s.version)

  useEffect(() => {
    if (!userId) {
      publishTenantAppIdentity('account', null)
      return
    }
    if (brandVersion > 0) invalidateCache(cacheKey(userId))

    let alive = true
    fetchAppIdentityForMe(userId).then((row) => {
      if (!alive) return
      // Both halves required. A tenant with no icon of its own must NOT get a
      // rewritten link: task 54's roll-out guard is that a tenant without a
      // mark offers no install rather than offering ours, and on iOS and in
      // the Dock that mark is frozen at install.
      if (!row?.manifest_query || !row.app_icon_url) {
        publishTenantAppIdentity('account', null)
        return
      }
      publishTenantAppIdentity('account', {
        manifestQuery: row.manifest_query,
        appIconUrl: row.app_icon_url,
        shortName: row.short_name,
        themeColor: row.theme_color,
      })
    })

    return () => {
      alive = false
      publishTenantAppIdentity('account', null)
    }
  }, [userId, brandVersion])
}
